import React, { useState, createContext, useContext } from 'react';
import { useTasks } from './TaskContext';
export interface Activity {
  id: number;
  type: string;
  date: string;
  title: string;
  description: string;
  author?: string;
  customerId: string; // Link activities to customers
  taskMeta?: {
    assignee?: string;
    dueDate?: string;
    priority?: 'Low' | 'Medium' | 'High' | 'Urgent';
    status?: 'Open' | 'In Progress' | 'Complete';
    linkedEntity?: string;
    isOverdue?: boolean;
  };
}
interface ActivityContextType {
  getActivitiesByCustomer: (customerId: string) => Activity[];
  getAllNotes: () => Activity[];
  addActivity: (customerId: string, activity: Omit<Activity, 'id' | 'customerId'>) => void;
}
const ActivityContext = createContext<ActivityContextType | undefined>(undefined);
// Initial mock activities with customer IDs
const initialActivities: Activity[] = [{
  id: 1,
  type: 'payment_success',
  date: '2025-11-30',
  title: 'Payment Successful',
  description: 'Charge of $126.50 was successful.',
  customerId: '1' // Sarah Johnson
}, {
  id: 2,
  type: 'product_swap',
  date: '2025-11-28',
  title: 'Product Swapped',
  description: 'Customer swapped 2024 Shiraz for 2025 Chardonnay.',
  customerId: '1'
}, {
  id: 3,
  type: 'box_change',
  date: '2025-11-25',
  title: 'Subscription Updated',
  description: 'Changed from 6 bottles to 12 bottles per delivery.',
  customerId: '1'
}, {
  id: 4,
  type: 'payment_method_updated',
  date: '2025-11-20',
  title: 'Payment Method Updated',
  description: 'Credit card ending in 4242 was updated.',
  customerId: '1'
}, {
  id: 5,
  type: 'cycle_skipped',
  date: '2025-11-15',
  title: 'Cycle Skipped',
  description: 'Customer skipped the December delivery cycle.',
  customerId: '1'
}, {
  id: 6,
  type: 'email_sent',
  date: '2025-11-10',
  title: 'Email Sent',
  description: 'Upcoming subscription reminder sent to customer.',
  customerId: '1'
}, {
  id: 7,
  type: 'note',
  date: '2025-11-05',
  title: 'Admin Note',
  description: 'Customer called to confirm delivery address change.',
  author: 'Sarah Admin',
  customerId: '1' // Sarah Johnson
}, {
  id: 8,
  type: 'payment_success',
  date: '2025-10-30',
  title: 'Payment Successful',
  description: 'Charge of $126.50 was successful.',
  customerId: '1'
}, {
  id: 9,
  type: 'note',
  date: '2026-01-07',
  title: 'Admin Note',
  description: 'Prefers reds only, no Chardonnay',
  author: 'Sarah Admin',
  customerId: '2' // James Wilson
}, {
  id: 10,
  type: 'note',
  date: '2026-01-07',
  title: 'Admin Note',
  description: 'Called about failed payment, updating card tomorrow',
  author: 'Mike Johnson',
  customerId: '3' // Emma Thompson
}, {
  id: 11,
  type: 'note',
  date: '2026-01-06',
  title: 'Admin Note',
  description: 'Upgraded to 12-bottle case for holiday season',
  author: 'Sarah Admin',
  customerId: '4' // Alice Johnson
}, {
  id: 12,
  type: 'note',
  date: '2026-01-06',
  title: 'Admin Note',
  description: 'Requested delivery date change to avoid vacation period',
  author: 'Mike Johnson',
  customerId: '5' // Robert Fox
}, {
  id: 13,
  type: 'note',
  date: '2026-01-07',
  title: 'Admin Note',
  description: 'Wants to try the new Pinot Noir in next shipment — added to preferences',
  author: 'Sarah Admin',
  customerId: '1' // Sarah Johnson
}, {
  id: 14,
  type: 'note',
  date: '2026-01-05',
  title: 'Admin Note',
  description: 'Mentioned interest in gift subscriptions for family members',
  author: 'Chris Taylor',
  customerId: '2' // James Wilson
}, {
  id: 15,
  type: 'note',
  date: '2026-01-04',
  title: 'Admin Note',
  description: 'Allergic to sulfites — flag all future shipments for low-sulfite wines only',
  author: 'Mike Johnson',
  customerId: '4' // Alice Johnson
}, {
  id: 16,
  type: 'note',
  date: '2026-01-03',
  title: 'Admin Note',
  description: 'VIP customer — always include handwritten thank you card with shipments',
  author: 'Sarah Admin',
  customerId: '5' // Robert Fox
}, {
  id: 17,
  type: 'note',
  date: '2026-01-07',
  title: 'Admin Note',
  description: 'Confirmed new shipping address: 45 Collins St, Melbourne VIC 3000',
  author: 'Chris Taylor',
  customerId: '3' // Emma Thompson
}];
export function ActivityProvider({
  children
}: {
  children: ReactNode;
}) {
  const [activities, setActivities] = useState<Activity[]>(initialActivities);
  const {
    getTasksByLinkedEntity
  } = useTasks();
  const getActivitiesByCustomer = (customerId: string): Activity[] => {
    // Get tasks linked to this customer
    const linkedTasks = getTasksByLinkedEntity(customerId);
    // Convert tasks to Activity format
    const taskActivities: Activity[] = linkedTasks.map(task => ({
      id: parseInt(task.id.replace('task-', '')) || Date.now() + Math.random(),
      type: 'task',
      date: new Date(task.createdAt).toISOString().split('T')[0],
      title: task.title,
      description: `Task assigned to ${task.assignee.name} - Priority: ${task.priority}. ${task.status === 'Complete' ? `Completed ${task.completedAt ? new Date(task.completedAt).toISOString().split('T')[0] : ''}` : `Due: ${task.dueDate}`}`,
      author: task.createdBy,
      customerId: customerId,
      taskMeta: {
        assignee: task.assignee.name,
        dueDate: task.dueDate,
        priority: task.priority,
        status: task.status,
        linkedEntity: task.linkedTo?.label,
        isOverdue: task.status !== 'Complete' && new Date(task.dueDate) < new Date('2026-02-12') // Hardcoded "today" check
      }
    }));
    const allActivities = [...activities, ...taskActivities];
    return allActivities.filter(activity => activity.customerId === customerId).sort((a, b) => {
      // Sort by date descending (newest first)
      // Handle both YYYY-MM-DD and DD-MM-YYYY formats if necessary, though ISO is preferred
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB.getTime() - dateA.getTime();
    });
  };
  const getAllNotes = (): Activity[] => {
    return activities.filter(activity => activity.type === 'note').sort((a, b) => {
      // Sort by date descending (newest first)
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB.getTime() - dateA.getTime();
    });
  };
  const addActivity = (customerId: string, activity: Omit<Activity, 'id' | 'customerId'>) => {
    const newActivity: Activity = {
      ...activity,
      id: Date.now(),
      customerId
    };
    setActivities(prev => [newActivity, ...prev]);
  };
  return <ActivityContext.Provider value={{
    getActivitiesByCustomer,
    getAllNotes,
    addActivity
  }} data-id="element-3952">
      {children}
    </ActivityContext.Provider>;
}
export function useActivities() {
  const context = useContext(ActivityContext);
  if (!context) {
    throw new Error('useActivities must be used within an ActivityProvider');
  }
  return context;
}
// Customer name mapping
export const customerNames: Record<string, string> = {
  '1': 'Sarah Johnson',
  '2': 'James Wilson',
  '3': 'Emma Thompson',
  '4': 'Alice Johnson',
  '5': 'Robert Fox'
};